import { Check } from 'lucide-react';
import { SecondaryButton, TextButton } from '../components/Button';
import { Card } from '../components/Card';
import { Screen } from '../components/Screen';
import { StatusChip, type ChipTone } from '../components/Status';
import { copy, SERVER_REASONS } from '../copy';
import { openExternal, telHref } from '../lib/links';
import { formatClock } from '../lib/time';
import { useAppStore } from '../state/store';
import type { QueueItem } from '../state/types';

const PLANNING_PHONE: string = import.meta.env.VITE_PLANNING_PHONE ?? '';

const CHIP: Record<QueueItem['state'], { tone: ChipTone; text: string }> = {
  waiting: { tone: 'neutral', text: copy.status.queueWaiting },
  sending: { tone: 'neutral', text: copy.status.queueSending },
  sent: { tone: 'sent', text: copy.status.queueSent },
  failed: { tone: 'waiting', text: copy.status.queueFailed },
};

function titleFor(item: QueueItem): string {
  const builder = copy.queue.itemTitles[item.type];
  return builder(item.subject ?? '');
}

/** J1–J3 — Nog te versturen. Het lege scherm is het doel, niet een randgeval. */
export function QueueScreen() {
  const queue = useAppStore((s) => s.queue);
  const navigate = useAppStore((s) => s.navigate);
  const tryQueueNow = useAppStore((s) => s.tryQueueNow);

  const items = queue.filter((item) => item.state !== 'sent' || item.sentAt !== null);

  return (
    <Screen
      name="queue"
      foot={
        items.length > 0 ? (
          <SecondaryButton onClick={() => void tryQueueNow()}>{copy.queue.tryNow}</SecondaryButton>
        ) : undefined
      }
    >
      <div className="row row--between pad-x" style={{ paddingTop: 14 }}>
        <h1 className="t-serif t-titel">{copy.queue.title}</h1>
        <TextButton onClick={() => navigate('day')}>{copy.queue.close}</TextButton>
      </div>

      {items.length === 0 ? (
        <div className="screen__body screen__body--center pad-x">
          <div className="stack" style={{ gap: 12, alignItems: 'flex-start' }}>
            <span
              aria-hidden
              style={{
                width: 64,
                height: 64,
                borderRadius: 'var(--radius-pill)',
                background: 'var(--color-mint)',
                color: 'var(--on-accent)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Check size={30} strokeWidth={2} />
            </span>
            <h2 className="t-serif" style={{ fontSize: 24 }}>
              {copy.queue.emptyTitle}
            </h2>
            <p className="o-65" style={{ fontSize: 16, margin: 0 }}>
              {copy.queue.emptyBody}
            </p>
          </div>
        </div>
      ) : (
        <>
          <p className="o-65 pad-x" style={{ fontSize: 16, paddingTop: 4 }}>
            {copy.queue.intro}
          </p>
          <div className="stack pad-x" style={{ paddingTop: 12, paddingBottom: 12 }}>
            {items.map((item) => {
              const chip = CHIP[item.state];
              const reason =
                item.state === 'failed'
                  ? (SERVER_REASONS[item.errorCode ?? ''] ?? item.error)
                  : null;
              return (
                <Card key={item.clientActionId} tone={item.state === 'failed' ? 'warning' : 'plain'}>
                  <div className="row row--between" style={{ alignItems: 'flex-start' }}>
                    <span style={{ fontSize: 17, fontWeight: 600 }}>{titleFor(item)}</span>
                    <span className="listrow__time">{formatClock(item.occurredAt)}</span>
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <StatusChip tone={chip.tone}>{chip.text}</StatusChip>
                  </div>
                  {reason ? (
                    <>
                      <p className="t-caption o-65" style={{ marginTop: 10, marginBottom: 10 }}>
                        {reason}
                      </p>
                      <SecondaryButton
                        disabledReason={PLANNING_PHONE ? null : copy.stop.noPhone}
                        onClick={() => openExternal(telHref(PLANNING_PHONE))}
                      >
                        {copy.queue.reportToPlanning}
                      </SecondaryButton>
                    </>
                  ) : null}
                </Card>
              );
            })}
          </div>
        </>
      )}
    </Screen>
  );
}
